/**
 * Message decrypt/encrypt façade — passphrase crypto removed; everything goes through E2E.
 */

import {
  decryptDmMessage,
  peekChatHasSession,
  resolveOwnPlaintext,
} from './e2e';

export { decryptDmMessage as decryptMessage, resolveOwnPlaintext };

const asCannotDecrypt = (msg) => ({
  ...msg,
  message: '🔒 cannot decrypt',
  encrypted: true,
  e2e: true,
});

export const decryptMessages = async (messages, chatId = null, options = {}) => {
  if (!Array.isArray(messages)) {
    return [];
  }
  const localUserId = options.localUserId ?? null;
  // Never throw — a single IDB/decrypt failure must not blank the whole chat.
  let startIdx = 0;
  try {
    if (chatId && !(await peekChatHasSession(chatId))) {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        const header = typeof msg?.e2e_header === 'string'
          ? (() => { try { return JSON.parse(msg.e2e_header); } catch (_) { return null; } })()
          : msg?.e2e_header;
        if (header?.type === 'prekey') {
          startIdx = i;
          break;
        }
      }
    }
  } catch (_) {
    startIdx = 0;
  }

  const out = [];
  for (let i = 0; i < messages.length; i += 1) {
    const msg = messages[i];
    const withChat = chatId && msg && !msg.chat_id ? { ...msg, chat_id: chatId } : msg;
    try {
      let decrypted;
      if (i < startIdx) {
        decrypted = await decryptDmMessage(withChat, {
          allowSessionReset: false,
          historySkipRatchet: true,
          localUserId,
        });
      } else {
        decrypted = await decryptDmMessage(withChat, {
          allowSessionReset: false,
          localUserId,
        });
      }
      if (
        decrypted
        && typeof decrypted.message === 'string'
        && (decrypted.message.startsWith('🔒') || decrypted.message.includes('cannot decrypt'))
      ) {
        const own = resolveOwnPlaintext(withChat);
        if (own) {
          decrypted = {
            ...decrypted,
            message: own,
            encrypted: true,
            e2e: true,
          };
        }
      }
      out.push(decrypted || withChat);
    } catch (_) {
      out.push(asCannotDecrypt(withChat || msg));
    }
  }
  return out;
};

/** @deprecated passphrase removed — kept as no-ops for any stray imports */
export const hasChatPassphrase = () => false;
export const getChatPassphrase = () => '';
export const setChatPassphrase = () => {};
export const clearChatPassphrase = () => {
  if (typeof document === 'undefined') return;
  const security = (() => {
    const { protocol, hostname } = window.location;
    const isLocalHttp = protocol === 'http:'
      && (hostname === 'localhost' || hostname === '127.0.0.1');
    if (protocol === 'https:' || isLocalHttp) return 'SameSite=Strict; Secure';
    return 'SameSite=Strict';
  })();
  document.cookie = `lynx_chat_passphrase=; Path=/; Max-Age=0; ${security}`;
  try {
    localStorage.removeItem('lynx.chatPassphrase');
  } catch (_) {
    // ignore
  }
};
export const subscribeChatCryptoChanged = () => () => {};

export const encryptText = async (plaintext) => plaintext;

export const encryptVoiceBlob = async (blob) => ({ blob, encrypted: false });

export const isEncryptedVoiceBytes = () => false;

export const decryptVoiceBytes = async (input, fallbackMime = 'audio/webm') => {
  const view = input instanceof Uint8Array ? input : new Uint8Array(input || []);
  return { bytes: view, mime: fallbackMime, encrypted: false };
};
